/* eslint-disable @next/next/no-img-element */
import { Transition } from '@headlessui/react';
import Camera from 'components/Icons/Camera';
import CrossMark from 'components/Icons/CrossMark';
import Download from 'components/Icons/Download';
import Recycle from 'components/Icons/Recycle';
import Share from 'components/Icons/Share';
import OverlayPortal from 'components/Others/OverlayPortal';
import UIButton from 'components/Others/UIButton';
import useInterval from 'hooks/useInterval';
import { FC, useEffect, useState } from 'react';
import {
  canShareFile,
  convertCanvasToBase64,
  downloadURL,
  shareBase64Image
} from 'utils/common.utils';
import * as ga from '../../lib/ga';

const PDCaptureOverlay: FC<{
  device: 'mobile' | 'desktop';
  hideOverlay: () => void;
}> = ({ device, hideOverlay }) => {
  const [isCaptureLoading, setIsCaptureLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [capturedPhotoDataURL, setCapturedPhotoDataURL] = useState('');
  const [isFlashElementVisible, setIsFlashElementVisible] = useState(false);
  const [isCountdownEnabled, setIsCountdownEnabled] = useState(false);

  const toggleFlash = async () => {
    const FLASH_MS = 750;
    setIsFlashElementVisible(true);
    setTimeout(() => {
      setIsFlashElementVisible(false);
    }, FLASH_MS);
    await new Promise((resolve) => setTimeout(resolve, FLASH_MS / 2));
  };

  const capture = async () => {
    setIsCountdownEnabled(false);
    setIsCaptureLoading(true);
    const canvasElement = document.querySelector(
      '.PD-canvas'
    ) as HTMLCanvasElement;
    await toggleFlash();
    const imageDataURL = await convertCanvasToBase64({
      width: device === 'desktop' ? 1024 : window.innerWidth * 2,
      height: device === 'desktop' ? 1024 : window.innerHeight * 2,
      element: canvasElement,
      flip: true,
      watermark: true
    });
    setCapturedPhotoDataURL(imageDataURL);
    setIsCaptureLoading(false);
  };

  const captureClickHandler = () => {
    ga.sendUICustomEvent({
      eventCategory: 'PD',
      eventAction: 'PD Overlay Click',
      eventLabel: 'Capture'
    })
    setIsCaptureLoading(true);
    setIsCountdownEnabled(true);
  };

  const downloadClickHandler = async () => {
    ga.sendUICustomEvent({
      eventCategory: 'PD',
      eventAction: 'PD Overlay Click',
      eventLabel: 'Download'
    })
    setIsDownloadLoading(true);
    try {
      await downloadURL(
        capturedPhotoDataURL,
        'dresden-PD-capture-' + new Date().getTime() + '.png'
      );
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const backToCameraClickHandler = () => {
    setCapturedPhotoDataURL('');
  };

  return (
    <>
      <Transition
        enter="transition-opacity duration-[250ms]"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-[400ms]"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={isFlashElementVisible}
        appear
        className="absolute top-0 left-0 z-[3] h-full w-full bg-white"
      />
      {isCountdownEnabled && (
        <PDCaptureCountdown timerSeconds={3} onCountdownCompleted={capture} />
      )}
      {device === 'desktop' ? (
        <PDDesktopCaptureOverlay
          captureClickHandler={captureClickHandler}
          isCaptureLoading={isCaptureLoading}
          isDownloadLoading={isDownloadLoading}
          capturedPhotoDataURL={capturedPhotoDataURL}
          downloadClickHandler={downloadClickHandler}
          backToCameraClickHandler={backToCameraClickHandler}
          hideOverlay={hideOverlay}
        />
      ) : (
        <PDMobileCaptureOverlay
          captureClickHandler={captureClickHandler}
          isCaptureLoading={isCaptureLoading}
          isDownloadLoading={isDownloadLoading}
          capturedPhotoDataURL={capturedPhotoDataURL}
          downloadClickHandler={downloadClickHandler}
          backToCameraClickHandler={backToCameraClickHandler}
          hideOverlay={hideOverlay}
        />
      )}
    </>
  );
};

interface PDPhotoCaptureOverlayProps {
  capturedPhotoDataURL: string;
  isCaptureLoading: boolean;
  isDownloadLoading: boolean;
  captureClickHandler: () => void;
  downloadClickHandler: () => void;
  backToCameraClickHandler: () => void;
  hideOverlay: () => void;
}

const PDMobileCaptureOverlay: FC<PDPhotoCaptureOverlayProps> = ({
  capturedPhotoDataURL,
  isCaptureLoading,
  isDownloadLoading,
  captureClickHandler,
  downloadClickHandler,
  backToCameraClickHandler,
  hideOverlay
}) => {
  const [isBrowserSupportsShareAPI, setIsBrowserSupportsShareAPI] =
    useState(false);

  const [isShareLoading, setIsShareLoading] = useState(false);

  useEffect(() => {
    if (!capturedPhotoDataURL) return;

    canShareFile(capturedPhotoDataURL, 'dresden-capture.png')
      .then((value) => setIsBrowserSupportsShareAPI(value))
      .catch(() => setIsBrowserSupportsShareAPI(false));
  }, [capturedPhotoDataURL]);

  // fix disappear on transition end image
  const [tempPreviewImage, setTempPreviewImage] = useState('');

  useEffect(() => {
    if (capturedPhotoDataURL) setTempPreviewImage(capturedPhotoDataURL);
  }, [capturedPhotoDataURL]);

  const shareClickHandler = async () => {
    try {
      setIsShareLoading(true);
      await shareBase64Image(
        capturedPhotoDataURL,
        'dresden-PD-capture-' + new Date().getTime() + '.png'
      );
    } catch {
      alert('Your browser does not support share.');
    } finally {
      setIsShareLoading(false);
    }
  };

  return (
    <div className="z-[3] flex h-full w-full">
      {false && (
        <div className="relative top-[60px] flex w-full justify-end px-[16px] pt-[18px]">
          <UIButton
            loading={isCaptureLoading}
            size="small"
            light
            customFontSize
            onClick={captureClickHandler}
            className="!h-[42px] !w-[42px] !rounded-full border-none !px-0">
            <Camera className="mt-[1px]" fill="currentColor" />
          </UIButton>
        </div>
      )}
      <OverlayPortal>
        <Transition
          show={!!capturedPhotoDataURL}
          appear
          enter="transition ease-in-out duration-300 transform"
          enterFrom="translate-x-[500px] opacity-0"
          enterTo="translate-x-0 opacity-100"
          leave="transition ease-in-out duration-[500ms] transform"
          leaveFrom="translate-y-0 opacity-100"
          leaveTo="translate-x-[500px] opacity-0"
          className="fixed top-0 left-0 z-[999999] flex h-full w-full items-end pb-[20px]">
          <span
            onClick={hideOverlay}
            className="absolute right-[16px] top-[16px] z-50 cursor-pointer rounded-full bg-white p-[12px] text-brand-black100 lg:hover:text-brand-orange">
            <CrossMark width={18} height={18} fill="currentColor" />
          </span>
          <img
            src={tempPreviewImage}
            className="absolute top-0 left-0 h-full w-full object-cover"
            alt="Captured photo"
          />
          <div className="relative z-[1] grid w-full grid-cols-[1fr,auto] px-[14px]">
            <div className="flex">
              {isBrowserSupportsShareAPI && (
                <UIButton
                  light
                  onClick={shareClickHandler}
                  icon={<Share fill="currentColor" />}
                  insetLoading
                  size="small"
                  loading={isShareLoading}
                  className="mr-[12px] !px-[12px] text-[12px]"
                  customFontSize>
                  Share
                </UIButton>
              )}
              <UIButton
                light
                onClick={downloadClickHandler}
                customFontSize
                size="small"
                loading={isDownloadLoading}
                className="!px-[12px] text-[12px]">
                <div className="mt-[3px] flex items-center">
                  <Download className="mt-[3px]" fill="currentColor" />
                  {!isBrowserSupportsShareAPI && (
                    <span className="ml-[8px]">Download</span>
                  )}
                </div>
              </UIButton>
            </div>
            <div className="ml-auto flex">
              <UIButton
                light
                onClick={backToCameraClickHandler}
                size="small"
                className="!px-[12px] text-[12px]"
                customFontSize>
                <div className="mt-[3px] flex items-center">
                  <Recycle className="mr-[8px] mt-[3px]" fill="currentColor" />
                  <span>Retake</span>
                </div>
              </UIButton>
            </div>
          </div>
        </Transition>
      </OverlayPortal>
    </div>
  );
};

const PDCaptureCountdown: FC<{
  timerSeconds: number;
  onCountdownCompleted: () => void;
}> = ({ timerSeconds, onCountdownCompleted }) => {
  const [isCountdownVisible, setIsCountdownVisible] = useState(true);

  const [countdownSecond, setCountdownSecond] = useState(timerSeconds);
  useInterval(() => {
    if (countdownSecond === 0) {
      onCountdownCompleted();
      setIsCountdownVisible(false);
      return;
    }
    setIsCountdownVisible(false);
    setTimeout(() => {
      setCountdownSecond(countdownSecond - 1);
      setIsCountdownVisible(true);
    }, 500);
  }, 1250);
  return (
    <div className="absolute left-0 top-0 flex h-full  w-full items-center justify-center opacity-75">
      <Transition
        appear
        show={isCountdownVisible}
        enter="transition-all duration-[400ms]"
        enterFrom="opacity-0 transform scale-0"
        enterTo="opacity-100 scale-100"
        leave="transition-all duration-[200ms]"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-0"
        className="mb-[60px] text-[72px] font-medium text-white sm:mb-0">
        {countdownSecond === 0 ? 'Smile' : countdownSecond}
      </Transition>
    </div>
  );
};

const PDDesktopCaptureOverlay: FC<PDPhotoCaptureOverlayProps> = ({
  capturedPhotoDataURL,
  isCaptureLoading,
  captureClickHandler,
  downloadClickHandler,
  backToCameraClickHandler
}) => {
  return (
    <div className="relative flex h-full z-50 items-end text-brand-black100">

    </div>
  );
};

export default PDCaptureOverlay;
